import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Stack,
  IconButton,
  Divider,
  alpha,
  useTheme,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Assignment as CareIcon,
  ArrowForwardIos as ArrowIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as ActiveIcon,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { QK } from "../../../base/qk";
import { useParams } from "react-router-dom";
import { getActiveCarePlans } from "../../../api/services/carePathWay";

const CarePlan = () => {
  const theme = useTheme();
  const { id: clientID } = useParams();

  const { data: carePlans = [], isLoading } = useQuery(
    [QK.activeCarePlans, { client: clientID }],
    () => getActiveCarePlans({ client: clientID }),
    { enabled: !!clientID }
  );

  if (isLoading) {
    return (
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: "12px",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
        overflow: "hidden",
      }}
    >
      <CardHeader
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: "#FAFBFC",
          borderBottom: "1px solid #F2F4F7",
        }}
        title={
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "6px",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CareIcon sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#1A1A2E", fontSize: "0.85rem" }}>
                Care Plan
              </Typography>
              <Chip
                label={carePlans.length}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.55rem",
                  fontWeight: 500,
                  bgcolor: "#F2F4F7",
                  color: "#667085",
                }}
              />
            </Box>
            <IconButton
              size="small"
              to={`/personal-details`}
              sx={{
                width: 28,
                height: 28,
                bgcolor: "#fff",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                "&:hover": {
                  bgcolor: theme.palette.primary.main,
                  color: "#fff",
                },
                transition: "all 0.2s",
              }}
            >
              <ArrowIcon sx={{ fontSize: 9 }} />
            </IconButton>
          </Box>
        }
      />

      <Divider sx={{ opacity: 0.4 }} />

      <CardContent sx={{ p: 1.5 }}>
        {carePlans.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              No active care plans
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0.5}>
            {carePlans.map((plan, index) => (
              <Box
                key={plan.id || index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  py: 0.75,
                  px: 0.5,
                  borderRadius: "4px",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: "4px",
                      bgcolor: "#D1FAE5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <ActiveIcon sx={{ fontSize: 12, color: "#10B981" }} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#1A1A2E",
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      flex: 1,
                    }}
                  >
                    {plan.form_title || plan.title || "Care Plan"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                  <CalendarIcon sx={{ fontSize: 11, color: "#98A2B3" }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#667085",
                      fontSize: "0.7rem",
                      fontWeight: 500,
                    }}
                  >
                    {plan.assigned_at ? new Date(plan.assigned_at).toLocaleDateString() : "N/A"}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default CarePlan;